/* eslint-disable camelcase */
/* eslint-disable prefer-destructuring */
import moment from '@narvar/moment-shim';
import { retailerSettingsService } from '../retailer-settings/retailer-settings';
import { getUrlInfo } from '../../lib/url';
import {
  createLabelItems,
  appendParamsToUrl,
  getEligibleItems,
  getEligibleItemsByReturnDate,
  getFinalReduce,
  getIneligibleItems,
  getUrlDetails,
} from '../order-info/utils';
import { receiveOrderCurrency, receiveOrderData } from '../../actions/orderInfo';
import { receiveOrderDataAddresses } from '../../actions/addresses';
import { enableSpecialHandling } from '../../actions/restrictions';
import { fetchUserDetails } from '../../actions/userPreferences';
import { getDateTime, isSameDate } from '../../lib/dates';
import { store } from '../../store';
import {
  setItems,
  setItemsByReturnDate,
  setItemsByReturnDay,
  setItemsGroupingItems,
  setNaItems,
  setRetailerEligibilities,
  setReturnLabels,
} from '../../actions/orderService';
import { fetchActionTypeReasons } from '../../actions/reasons';
import { ItemEventStatus, ItemsLayouts, ReturnDateViewType } from '../../constants/enums';

const orderServiceFunction = () => {
  const totalReturned = {};
  const { hostURL } = getUrlDetails();
  const disabled = retailerSettingsService.getDisabled();
  const exchangeEnabled = retailerSettingsService.getIsExchangeEnabled();
  const isMultiOrder = retailerSettingsService.getIsMultiOrder();
  let myOrder = {};
  let orderNumber = '';
  let customerId = null;
  let retailerName = '';
  let returnDate = {};
  let allItems = [];
  let formData = {};
  let orderOverrideCode = null;
  let confirmOrder = {};
  let isGiftReturn = false;
  let eligibleItemsCount = 0;
  let ineligibleItemsCount = 0;
  let orderParams = {};
  let shipmentListData = [];

  const validateZip = ({ postalCode, invalidStates, store }) => {
    const {
      page: { browserFingerprint },
    } = store.getState();
    const url = `${hostURL}/validate/zip?ozip=${postalCode}&restricted_states=${invalidStates.join(
      ',',
    )}`;
    const returnVal = fetch(url, {
      method: 'GET',
      headers: {
        'narvar-tfid': browserFingerprint,
      },
    })
      .then((response) => response.json())
      .then((data) => !(data.restricted || data.military));

    return returnVal;
  };

  /**
   * Checks if all items have the same max_return_date and removes the date if they do.
   * If the items have different dates, returns the original array.
   */
  const checkAndHandleMaxReturnDate = (items) => {
    if (!items || items.length === 0 || retailerSettingsService.getReturnDateViewType() !== ReturnDateViewType.Dynamic)
      return items;

    const nonReturnedItems = items.filter((item) => item.returned === false);

    if (nonReturnedItems.length === 0) {
      return items;
    }

    const firstDate = nonReturnedItems[0].max_return_date;
    const allSameDate = nonReturnedItems.every(
      (item) => item.max_return_date && isSameDate(item.max_return_date, firstDate),
    );

    if (allSameDate) {
      return items.map((item) => {
        const { max_return_date, ...itemWithoutDate } = item;
        return itemWithoutDate;
      });
    }

    return items;
  };

  const validateOrder = async (props) => {
    const {
      page: {
        productType,
        browserFingerprint,
        locale,
        deferOverride: deferOverrideState,
        initialPageLoadParams,
      },
      returnsSettings: { [productType]: { additionalProcessParams = [] } = {} },
      language,
      claims: { enabled: claimsEnabled } = {},
      itemsPage: { itemsLayout } = {},
    } = store.getState();

    const product = props?.product || productType;
    const orderData = { ...(props?.order || storedOrder()) };
    let deferOverride = deferOverrideState;

    if (props?.deferOverride === false) {
      deferOverride = false;
    }

    // TODO: Delete all the deletes once serialization for all retailers is done.
    const RETAILER = disabled ? 'peninsula' : orderData.retailer;
    delete orderData.retailer;
    const ORDERNUMBER = window.encodeURIComponent(orderData.number);
    delete orderData.number;
    const RETURNOVERRIDE = !deferOverride ? orderData.code : null;
    delete orderData.code;
    const MOCKOMS = orderData.mockoms || null;
    delete orderData.mockoms;
    const VERSION = orderData.version;
    delete orderData.version;
    let BZIP = orderData.zip || null;
    delete orderData.zip;
    // TODO: Remove this after standardizing on URL params.
    const DZIP = orderData.dzip || null;
    delete orderData.dzip;
    // Angular encodes '+' to %20, this workaround UTF-8 encodes all spaces and +
    const EMAIL = orderData.email ? orderData.email.replace(/\s|\+/g, '%2B') : null;
    delete orderData.email;
    const PHONE = orderData.phone || null;
    delete orderData.phone;
    const LOCALE = orderData.locale;
    delete orderData.locale;
    const { params: urlParams } = getUrlInfo();
    const skipLogin = retailerSettingsService.getSkipLogin();
    const { ignoreReturnLabels = false } = retailerSettingsService.getSettings();
    const timestamp = Date.now();
    let GIFT = false;
    let orderURL;
    let zipvar;
    let orderEndPoint = '';
    let additionalQueryParams = '';

    if (orderData.isGift) {
      GIFT = true;
      setGiftReturn(GIFT);
    }

    zipvar = orderData.isGift ? 'dzip' : 'bzip';

    // TODO: Remove this after standardizing on the URL params.
    zipvar = DZIP ? 'dzip' : zipvar;
    BZIP = DZIP || BZIP;

    additionalProcessParams.forEach((param, index, params) => {
      if (urlParams[param]) {
        additionalQueryParams = additionalQueryParams || '&';
        additionalQueryParams = `${additionalQueryParams}${param}=${encodeURIComponent(
          urlParams[param],
        )}`;

        if (index !== params.length - 1) {
          additionalQueryParams = `${additionalQueryParams}&`;
        }
      }
    });

    if (isMultiOrder) {
      orderEndPoint = `${hostURL}/returns/${RETAILER}/orders?`;
    } else {
      orderEndPoint = `${hostURL}/returns/${RETAILER}/order/${ORDERNUMBER}?`;
    }

    orderURL = `${orderEndPoint}${zipvar}=${BZIP}&return_override_code=${RETURNOVERRIDE}&email=${EMAIL}&phone=${PHONE}&version=${VERSION}&gift=${GIFT}&locale=${LOCALE}&product=${product}${additionalQueryParams}&skip_login=${skipLogin}&timestamp=${timestamp}&mockoms=${MOCKOMS}${initialPageLoadParams?.mode ? `&mode=${initialPageLoadParams.mode}` : ``}`;

    if (EMAIL === 'null' || EMAIL === null) {
      orderURL = `${hostURL}/returns/${RETAILER}/order/${ORDERNUMBER}?${zipvar}=${BZIP}&phone=${PHONE}&return_override_code=${RETURNOVERRIDE}&version=${VERSION}&gift=${GIFT}&locale=${LOCALE}&product=${product}&timestamp=${timestamp}&mockoms=${MOCKOMS}${initialPageLoadParams?.mode ? `&mode=${initialPageLoadParams.mode}` : ``}`;
    }

    orderOverrideCode = RETURNOVERRIDE;

    orderParams = {
      order_number: ORDERNUMBER || null,
      return_override_code: RETURNOVERRIDE || null,
      login_email: EMAIL || null,
      phone: PHONE || null,
      zip: BZIP || null,
      product: product || productType,
      additional_params: additionalQueryParams || null,
      skip_login: skipLogin,
      mockoms: MOCKOMS || null,
    };

    const fetchUrl = appendParamsToUrl(orderURL, orderData);

    const { authorization } = orderData;

    const responseData = fetch(fetchUrl, {
      method: 'GET',
      headers: {
        'narvar-tfid': browserFingerprint,
        ...(authorization ? { authorization } : {}),
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((res) => {
        const response = { data: res };
        if (response.data.order_info) {
          response.data.customer_info = response.data.customer_info || {};
          const {
            customer_info: customerInfo,
            order_info_items: orderInfoItems,
            order_info: {
              retailer,
              order_currency: orderCurrencyCode,
              order_locale: orderLocale,
              customer_ref_number: customerRefNumber,
              order_number: orderInfoOrderNumber,
              return_labels: returnLabels,
              ship_by_date: shipByDate,
              has_special_handling_item: hasSpecialHandlingItem,
            } = {},
            shipments_list,
            pickups_list,
          } = response.data;

          const shipmentList =
            pickups_list?.length > 0
              ? [
                  ...pickups_list.map((pickup) => ({
                    ...pickup,
                    shipment_items: pickup.pickup_items,
                  })),
                  ...shipments_list,
                ]
              : shipments_list;

          shipmentListData = shipmentList;

          const now = moment();
          const nonNullShipmentList = (shipmentList || []).filter(
            ({ ship_by_date }) => now.diff(moment(ship_by_date), 'days') <= 0,
          );
          const productToken = product.substr(0, product.length - 1);
          const retailerEligibilities = { [productToken]: true };
          if (exchangeEnabled) {
            retailerEligibilities.exchange = true;
          }
          if (claimsEnabled) {
            retailerEligibilities.claim = claimsEnabled;
          }

          myOrder = response.data;
          myOrder.shipments = nonNullShipmentList;
          allItems = checkAndHandleMaxReturnDate(orderInfoItems);

          customerId = customerRefNumber;
          retailerName = retailer;
          orderNumber = orderInfoOrderNumber;

          if (!isMultiOrder) {
            returnDate = getDateTime({ locale, date: shipByDate });
            store.dispatch(setReturnLabels(returnLabels));
          } else {
            store.dispatch(setReturnLabels(customerInfo.return_labels));
          }

          store.dispatch(
            setItems(
              getEligibleItems(
                allItems,
                retailerEligibilities,
                exchangeEnabled || claimsEnabled ? 'some' : 'every',
              ),
            ),
          );

          const itemGroupingItems = [];
          const ungroupedItems = [];

          allItems.forEach((item) => {
            if (item.item_events?.length) {
              item.item_events.forEach((currentItemEvent) => {
                if (currentItemEvent.event_type) {
                  itemGroupingItems.push({
                    ...item,
                    ...Object.fromEntries(
                      Object.entries(currentItemEvent).map(([key, val]) => [
                        'current_event_' + key,
                        val,
                      ]),
                    ),
                    current_event: {
                      ...currentItemEvent,
                    },
                  });
                }
              });
            } else {
              ungroupedItems.push(item);
            }
          });

          store.dispatch(setItemsGroupingItems(itemGroupingItems));

          store.dispatch(
            setNaItems(
              getIneligibleItems(ungroupedItems, retailerEligibilities).filter(
                (item) => item.remaining_qty > 0 || ignoreReturnLabels,
              ),
            ),
          );

          const {
            byDay,
            byTimestamp,
            ineligibleByDay = [],
          } = getEligibleItemsByReturnDate(
            allItems,
            nonNullShipmentList,
            retailerEligibilities,
            language,
            locale,
            exchangeEnabled,
          );
          store.dispatch(setItemsByReturnDate(byTimestamp));
          store.dispatch(setItemsByReturnDay(byDay));
          if (ineligibleByDay.length && itemsLayout === ItemsLayouts.ShipmentView) {
            store.dispatch(setNaItems([...getNaItems(), ...ineligibleByDay]));
          }

          eligibleItemsCount = getItems().length;
          ineligibleItemsCount = getNaItems().length;

          store.dispatch(
            receiveOrderData({
              currentOrderNumber: orderNumber,
              orderURL: getLabels(),
              customerInfo,
              orderData: myOrder,
              shipByDate: returnDate,
              validPostalCode: true,
            }),
          );

          store.dispatch(
            receiveOrderDataAddresses({
              customerInfo: response.data.customer_info,
              shipTo: response.data.ship_to,
              billTo: response.data.bill_to,
            }),
          );

          if (hasSpecialHandlingItem) {
            store.dispatch(enableSpecialHandling());
          }

          if (orderCurrencyCode || orderLocale) {
            store.dispatch(receiveOrderCurrency({ orderCurrencyCode, orderLocale }));
          }

          if (claimsEnabled) {
            store.dispatch(fetchActionTypeReasons());
          }

          createLabelItems({
            labels: getLabels(),
            retailerName,
            allItems,
            totalReturned,
          });

          store.dispatch(fetchUserDetails());
          store.dispatch(setRetailerEligibilities(retailerEligibilities));
          return response;
        }
        return response;
      })
      .catch((error) => ({ data: error, error: true }));

    return responseData;
  };

  const initSimpleReturn = (form) => {
    formData = {
      ...form,
      order_number: form.number,
      simple_return: true,
    };
    myOrder = {
      order_info: {
        email: form.email,
      },
      order_info_items: [],
    };
  };

  // Service methods
  const storeOrder = (order) => {
    formData = order;
  };

  const storedOrder = () => formData;

  const getOrder = () => myOrder;

  const getCustomerId = () => customerId;

  const getEligibleItemsCount = () => eligibleItemsCount;

  const getIneligibleItemsCount = () => ineligibleItemsCount;

  const getItems = () => {
    const {
      orderService: { items },
    } = store.getState();
    return items;
  };

  const getItemsByReturnDate = () => {
    const {
      orderService: { itemsByReturnDate },
    } = store.getState();
    return itemsByReturnDate;
  };

  const getItemIdByShipmentDate = () => {
    const {
      orderService: { itemsByReturnDate },
    } = store.getState();
    return (
      itemsByReturnDate?.map((dateGroup) =>
        Object.values(dateGroup.items).map(({ item_id }) => ({ item_id })),
      ) || []
    );
  };

  const getItemIdByTrackingNumber = () => {
    const {
      orderInfo: {
        orderData: { shipments_list: shipmentsList = [] },
      },
    } = store.getState();

    const trackingMap = new Map();

    shipmentsList.forEach(({ tracking_number, shipment_items }) => {
      if (!trackingMap.has(tracking_number)) {
        trackingMap.set(tracking_number, []);
      }
      const items = trackingMap.get(tracking_number);
      shipment_items.forEach(({ item_id }) => {
        items.push({ item_id });
      });
    });

    return Array.from(trackingMap.values());
  };

  const getItemsByReturnDay = () => {
    const {
      orderService: { itemsByReturnDay },
    } = store.getState();
    return itemsByReturnDay;
  };

  const getItemGroupingItems = () => {
    const {
      orderService: { itemGroupingItems },
    } = store.getState();
    return itemGroupingItems;
  };

  const getAllItemsCount = () => getItems().reduce((acc, val) => val.remaining_qty + acc, 0);

  const updateItems = (newItems) => {
    store.dispatch(setItems(newItems));
  };

  const getOrderParams = () => orderParams;

  const getLabels = () => store.getState()?.orderService?.returnLabels || [];

  const getTotalReturned = () => totalReturned;

  const getOrderOverrideCode = () => orderOverrideCode;

  const isOverrideCode = () => {
    if (orderOverrideCode != null && orderOverrideCode !== '') {
      return true;
    }

    return false;
  };

  const getNaItems = () => {
    const {
      orderService: { naItems },
    } = store.getState();
    return naItems;
  };

  const parseItemsByDate = () => {
    const {
      orderService: { itemsByReturnDate },
    } = store.getState();
    return itemsByReturnDate;
  };
  const parseItemsByDay = () => {
    const {
      orderService: { itemsByReturnDay },
    } = store.getState();
    return itemsByReturnDay;
  };

  const getReturnDate = () => returnDate;

  const removeLabel = (label) => {
    const labels = getLabels();
    const index = labels.indexOf(label);

    if (index !== -1) {
      labels.splice(index, 1);
    }
    store.dispatch(setReturnLabels(labels));
  };

  const setConfirmOrder = (order) => {
    confirmOrder = order;
  };

  const getConfirmOrder = () => confirmOrder;

  const extendConfirmOrder = (obj) => {
    confirmOrder = { ...confirmOrder, ...obj };
  };

  const setGiftReturn = (isGift) => (isGiftReturn = isGift);

  const getGiftReturn = () => isGiftReturn;

  const getShipmentList = () => shipmentListData;

  const getReturnAndEligibleByDate = (byDate = false) => {
    const iterable = byDate ? parseItemsByDate() : parseItemsByDay();
    if (!iterable || !iterable.length) return [];
    const itemsById = getItems().reduce(
      (acc, item) => ({
        ...acc,
        [item.product_sku]: {
          ...item,
          return_qty: item.return_qty || 0,
        },
      }),
      {},
    );
    const finalReduce = getFinalReduce({ iterable, itemsById });
    const eligibleArr = Object.keys(finalReduce).map((date) => ({
      ship_by_date: date,
      items: Object.keys(finalReduce[date]).map((sku) => ({
        ...finalReduce[date][sku],
      })),
    }));
    return eligibleArr;
  };

  return {
    validateZip,
    validateOrder,
    storeOrder,
    storedOrder,
    getOrder,
    getCustomerId,
    getItems,
    getItemsByReturnDate,
    getItemsByReturnDay,
    getItemIdByShipmentDate,
    getItemIdByTrackingNumber,
    updateItems,
    getLabels,
    getTotalReturned,
    getOrderOverrideCode,
    isOverrideCode,
    getNaItems,
    getItemGroupingItems,
    parseItemsByDate,
    parseItemsByDay,
    getReturnDate,
    removeLabel,
    setConfirmOrder,
    getConfirmOrder,
    extendConfirmOrder,
    setGiftReturn,
    getGiftReturn,
    getAllItemsCount,
    getEligibleItemsCount,
    getIneligibleItemsCount,
    getOrderParams,
    getReturnAndEligibleByDate,
    getShipmentList,
    initSimpleReturn,
  };
};

export const orderService = orderServiceFunction();
